import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { apiService } from '../../services/api';
import NotificationManagement from './NotificationManagement';

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  usersToday: number;
  usersThisWeek: number;
  usersThisMonth: number;
  recentlyActiveUsers: number;
  totalTasks: number;
  completedTasks: number;
}

interface User {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  phone: string;
  role: string;
  is_active: boolean;
  is_archived: boolean;
  archived_at: string;
  theme_slug: string;
  whatsapp_allowed: boolean;
  premium_themes_enabled: boolean;
  created_at: string;
  last_login_at: string;
  task_count: number;
  completed_tasks: number;
  last_activity: string;
}

interface Theme {
  id: string;
  name: string;
  slug: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  is_premium: boolean;
  is_active: boolean;
}

interface AppSettings {
  [key: string]: {
    value: string;
    type: string;
    description: string;
  };
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const { setTheme: applyTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'settings' | 'themes' | 'notifications'>('overview');
  const [stats, setStats] = useState<UserStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [defaultTheme, setDefaultTheme] = useState('purple');
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const fetchAdminData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsRes, usersRes, settingsRes, themesRes] = await Promise.all([
        apiService.getAdminStats(),
        apiService.getAdminUsers(showArchived),
        apiService.getAdminSettings(),
        apiService.getAdminThemes()
      ]);

      setStats(statsRes);
      setUsers(usersRes.users || usersRes);
      setSettings(settingsRes.settings);
      setThemes(themesRes.themes);
      
      if (settingsRes.settings?.welcome_message) {
        setWelcomeMessage(settingsRes.settings.welcome_message.value);
      }
      if (settingsRes.settings?.default_theme) {
        setDefaultTheme(settingsRes.settings.default_theme.value);
      }
    } catch (error: any) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        setError(`Access denied. Only admin users can access this page.`);
      } else {
        setError(error.response?.data?.error || 'Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  const handleToggleActive = async (userId: string) => {
    try {
      await apiService.toggleUserActive(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, is_active: !u.is_active } : u));
      showSuccess('User status updated');
    } catch (err) {
      setError('Failed to update user status');
    }
  };

  const handleArchiveUser = async (userId: string, email: string) => {
    if (!window.confirm(`Archive user "${email}"? This will deactivate the account. You can restore it later.`)) {
      return;
    }
    try {
      await apiService.archiveUser(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, is_archived: true, is_active: false } : u));
      showSuccess('User archived successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to archive user');
    }
  };

  const handleRestoreUser = async (userId: string) => {
    try {
      await apiService.restoreUser(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, is_archived: false, is_active: true } : u));
      showSuccess('User restored successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to restore user');
    }
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    if (!window.confirm(`PERMANENTLY DELETE user "${email}"?\n\nThis action CANNOT be undone. All user data will be lost.`)) {
      return;
    }
    if (!window.confirm(`Are you ABSOLUTELY SURE you want to delete "${email}"?\n\nType thinking time... Click OK only if certain.`)) {
      return;
    }
    try {
      await apiService.deleteUserPermanently(userId);
      setUsers(users.filter(u => u.id !== userId));
      showSuccess('User permanently deleted');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleToggleWhatsApp = async (userId: string) => {
    try {
      await apiService.toggleUserWhatsApp(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, whatsapp_allowed: !u.whatsapp_allowed } : u));
      showSuccess('WhatsApp access updated');
    } catch (err) {
      setError('Failed to update WhatsApp access');
    }
  };

  const handleTogglePremium = async (userId: string) => {
    try {
      await apiService.toggleUserPremiumThemes(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, premium_themes_enabled: !u.premium_themes_enabled } : u));
      showSuccess('Premium themes access updated');
    } catch (err) {
      setError('Failed to update premium access');
    }
  };

  const handleSaveWelcomeMessage = async () => {
    try {
      setSaving(true);
      await apiService.updateAdminSetting('welcome_message', welcomeMessage);
      showSuccess('Welcome message updated');
    } catch (err) {
      setError('Failed to save welcome message');
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefaultTheme = async (themeSlug: string) => {
    try {
      setSaving(true);
      await apiService.setDefaultTheme(themeSlug);
      setDefaultTheme(themeSlug);
      // Also apply the theme visually for the admin
      await applyTheme(themeSlug);
      showSuccess('Default theme updated and applied');
    } catch (err) {
      setError('Failed to set default theme');
    } finally {
      setSaving(false);
    }
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', padding: '2rem' }}>
        <div style={{ width: '48px', height: '48px', border: '4px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading admin dashboard...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', maxWidth: '700px', margin: '2rem auto' }}>
        <div style={{ backgroundColor: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '1.5rem' }}>
          <h2 style={{ color: '#dc2626', marginBottom: '1rem' }}>⚠️ Error</h2>
          <p style={{ color: '#7f1d1d', marginBottom: '1rem' }}>{error}</p>
          <button onClick={fetchAdminData} style={{ padding: '0.5rem 1rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>📊 Admin Dashboard</h1>
        <div style={{ fontSize: '0.875rem', color: '#6b7280', padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
          Logged in as: <strong>{user?.email}</strong>
        </div>
      </div>

      {successMsg && (
        <div style={{ backgroundColor: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', color: '#065f46' }}>
          ✅ {successMsg}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '0.5rem' }}>
        {[
          { id: 'overview', label: '📈 Overview' },
          { id: 'users', label: '👥 Users' },
          { id: 'settings', label: '⚙️ Settings' },
          { id: 'themes', label: '🎨 Themes' },
          { id: 'notifications', label: '🔔 Notifications' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: activeTab === tab.id ? '#3b82f6' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? 'bold' : 'normal',
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
          <StatCard title="Total Users" value={stats?.totalUsers || 0} color="#3b82f6" icon="👥" />
          <StatCard title="Active Users" value={stats?.activeUsers || 0} color="#10b981" icon="✓" />
          <StatCard title="New Today" value={stats?.usersToday || 0} color="#f59e0b" icon="🆕" />
          <StatCard title="This Week" value={stats?.usersThisWeek || 0} color="#8b5cf6" icon="📅" />
          <StatCard title="Total Tasks" value={stats?.totalTasks || 0} color="#06b6d4" icon="📝" />
          <StatCard title="Completed" value={stats?.completedTasks || 0} color="#ec4899" icon="✅" />
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>User Management</h2>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', color: '#6b7280' }}>
              <input 
                type="checkbox" 
                checked={showArchived} 
                onChange={(e) => setShowArchived(e.target.checked)}
                style={{ width: '16px', height: '16px' }}
              />
              Show Archived Users
            </label>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1100px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>WhatsApp</th>
                  <th style={thStyle}>Premium</th>
                  <th style={thStyle}>Tasks</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: u.is_archived ? '#fef2f2' : 'transparent', opacity: u.is_archived ? 0.8 : 1 }}>
                    <td style={tdStyle}>
                      <div>
                        {u.email}
                        {u.role === 'admin' && <span style={{ marginLeft: '6px', padding: '1px 6px', backgroundColor: '#7c3aed', color: 'white', borderRadius: '8px', fontSize: '0.65rem', fontWeight: 600 }}>ADMIN</span>}
                        {u.is_archived && <span style={{ marginLeft: '6px', padding: '1px 6px', backgroundColor: '#991b1b', color: 'white', borderRadius: '8px', fontSize: '0.65rem', fontWeight: 600 }}>ARCHIVED</span>}
                      </div>
                    </td>
                    <td style={tdStyle}>{u.first_name} {u.last_name}</td>
                    <td style={tdStyle}>
                      <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', backgroundColor: u.is_active ? '#d1fae5' : '#fee2e2', color: u.is_active ? '#065f46' : '#991b1b' }}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', backgroundColor: u.whatsapp_allowed ? '#dbeafe' : '#f3f4f6', color: u.whatsapp_allowed ? '#1e40af' : '#6b7280' }}>
                        {u.whatsapp_allowed ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', backgroundColor: u.premium_themes_enabled ? '#fef3c7' : '#f3f4f6', color: u.premium_themes_enabled ? '#92400e' : '#6b7280' }}>
                        {u.premium_themes_enabled ? '⭐ Yes' : 'No'}
                      </span>
                    </td>
                    <td style={tdStyle}>{u.task_count} ({u.completed_tasks} done)</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {!u.is_archived ? (
                          <>
                            <button onClick={() => handleToggleActive(u.id)} style={{ ...btnStyle, backgroundColor: u.is_active ? '#fee2e2' : '#d1fae5', color: u.is_active ? '#991b1b' : '#065f46' }}>
                              {u.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button onClick={() => handleToggleWhatsApp(u.id)} style={{ ...btnStyle, backgroundColor: '#dbeafe', color: '#1e40af' }}>
                              {u.whatsapp_allowed ? 'Disable WA' : 'Enable WA'}
                            </button>
                            <button onClick={() => handleTogglePremium(u.id)} style={{ ...btnStyle, backgroundColor: '#fef3c7', color: '#92400e' }}>
                              {u.premium_themes_enabled ? 'Remove Premium' : 'Add Premium'}
                            </button>
                            {u.role !== 'admin' && (
                              <button onClick={() => handleArchiveUser(u.id, u.email)} style={{ ...btnStyle, backgroundColor: '#fef2f2', color: '#991b1b' }}>
                                Archive
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            <button onClick={() => handleRestoreUser(u.id)} style={{ ...btnStyle, backgroundColor: '#d1fae5', color: '#065f46' }}>
                              Restore
                            </button>
                            <button onClick={() => handleDeleteUser(u.id, u.email)} style={{ ...btnStyle, backgroundColor: '#991b1b', color: 'white' }}>
                              Delete Forever
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '600px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>App Settings</h2>
          
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem', color: '#374151' }}>
              Welcome Message
            </label>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Text shown below "Welcome, [Name]!" on the dashboard
            </p>
            <textarea
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', minHeight: '80px', fontSize: '1rem' }}
              placeholder="Your daily planner is ready. Let's make today productive!"
            />
            <button
              onClick={handleSaveWelcomeMessage}
              disabled={saving}
              style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            >
              {saving ? 'Saving...' : '💾 Save Message'}
            </button>
          </div>
        </div>
      )}

      {/* Themes Tab */}
      {activeTab === 'themes' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>Theme Management</h2>
          
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
            Select the default theme for all users. Current default: <strong style={{ color: '#3b82f6' }}>{defaultTheme}</strong>
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {themes.map((theme) => (
              <div
                key={theme.id}
                style={{
                  border: defaultTheme === theme.slug ? '3px solid #3b82f6' : '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => handleSetDefaultTheme(theme.slug)}
              >
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: theme.primary_color }}></div>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: theme.secondary_color }}></div>
                  {theme.accent_color && <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: theme.accent_color }}></div>}
                </div>
                <p style={{ fontWeight: 'bold', margin: '0.5rem 0 0' }}>{theme.name}</p>
                {theme.is_premium && <span style={{ fontSize: '0.75rem', color: '#f59e0b' }}>⭐ Premium</span>}
                {defaultTheme === theme.slug && <span style={{ fontSize: '0.75rem', color: '#3b82f6', marginLeft: '0.5rem' }}>✓ Default</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <NotificationManagement />
      )}
    </div>
  );
};

const StatCard: React.FC<{ title: string; value: number; color: string; icon: string }> = ({ title, value, color, icon }) => (
  <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', borderLeft: `4px solid ${color}` }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>{title}</p>
        <p style={{ fontSize: '2rem', fontWeight: 'bold', color }}>{value}</p>
      </div>
      <div style={{ fontSize: '2.5rem' }}>{icon}</div>
    </div>
  </div>
);

const thStyle: React.CSSProperties = { padding: '12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#374151', textTransform: 'uppercase' };
const tdStyle: React.CSSProperties = { padding: '12px', fontSize: '0.875rem', color: '#6b7280' };
const btnStyle: React.CSSProperties = { padding: '4px 8px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500 };

export default AdminDashboard;
